import {
  Autocomplete,
  Stack,
  TextField,
} from '@mui/material';
import _ from 'lodash';
import { type FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import ReadFromRouter from './ReadFromRouter.js';
import RouterSimFields from './RouterSimFields.js';
import { simBodiesFromFormValues, simFormValuesFromRouter } from './routerSimForm.js';
import { useAllCameras, useCreateRouter, useNvrs, useOneRouter, usePatients, useUpdateRouter } from '../../api/index.js';
import { type Camera, type Nvr, type Patient } from '../../models/index.js';
import AutocompleteOptions from '../AutocompleteOptions.js';
import FormDialog from '../Forms/FormDialog.js';
import { type RouterFormValues } from '../Forms/formTypes.js';
import { routerFormSchema } from '../Forms/validations.js';

const defaultValues: RouterFormValues = {
  name: '',
  imei: '',
  username: '',
  password: '',
  patient: {} as Patient,
  cameras: [],
  nvrs: [],
  ...simFormValuesFromRouter(),
};

interface RouterFormProps {
  open: boolean;
  onClose: () => void;
  routerId?: string;
  refetch: () => void;
}
const RouterForm: FC<RouterFormProps> = ({ open, routerId, refetch, onClose }) => {
  const { data } = useOneRouter(routerId!, {
    enabled: !!routerId,
  });
  const { data: camerasData = [] } = useAllCameras();
  const { data: nvrsData = [] } = useNvrs();
  const { data: patientsData = [] } = usePatients();

  const createMutation = useCreateRouter();
  const updateMutation = useUpdateRouter();
  const formattedData: RouterFormValues = useMemo(() => {
    if (data) {
      return {
        name: data.name,
        imei: data.imei,
        username: data.username,
        password: data.password,
        patient: data.patient,
        cameras: data.cameras,
        nvrs: (data.nvrs ?? []) as Nvr[],
        ...simFormValuesFromRouter(data.sims),
      };
    }
    return defaultValues;
  }, [data]);

  return (
    <FormDialog
      open={open}
      name={'router'}
      onClose={onClose}
      data={formattedData}
      schema={routerFormSchema}
      isEditing={!!data}
      onSubmit={async ({ patient, cameras, nvrs, sims, primarySlot, ...data }, isEditing) => {
        if (isEditing) {
          await updateMutation.mutateAsync({
            id: routerId,
            ...data,
            patientId: patient?.id,
            cameraIds: cameras?.map((item: Camera) => item.id) || [],
            nvrIds: nvrs?.map((item: Nvr) => item.id) || [],
            sims: simBodiesFromFormValues({ sims, primarySlot }),
          });
        } else {
          await createMutation.mutateAsync({
            ...data,
            patientId: patient?.id,
            cameraIds: cameras?.map((item: Camera) => item.id) || [],
            nvrIds: nvrs?.map((item: Nvr) => item.id) || [],
            sims: simBodiesFromFormValues({ sims, primarySlot }),
          });
        }
      }}
      refetch={refetch}
    >
      {(control, errors) => (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Controller
            name={'name'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.name}
                helperText={errors?.name?.message || ''}
                label={'Name'}
              />
            )}
          />
          <Controller
            name={'username'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.username}
                helperText={errors?.username?.message || ''}
                label={'Username'}
              />
            )}
          />
          <Controller
            name={'password'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.password}
                type={'password'}
                helperText={errors?.password?.message || ''}
                label={'Password'}
              />
            )}
          />
          <RouterSimFields control={control} errors={errors} />
          <ReadFromRouter control={control} />
          <Controller
            name={'imei'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.imei}
                helperText={errors?.imei?.message || 'Filled in by Read from router, or type it from the router label'}
                label={'IMEI'}
              />
            )}
          />
          <Controller
            name={'patient'}
            control={control}
            render={({ field }) => {
              return (
                <Autocomplete
                  value={!_.isEmpty(field.value) ? field.value : null}
                  onChange={(_: any, value: Patient | null) => field.onChange(value)}
                  id={'patient-select'}
                  options={patientsData}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(patient: Patient) =>
                    !_.isEmpty(patient) ? `${patient.firstName} ${patient.lastName}` : ''
                  }
                  renderInput={(params) => <TextField {...params} label={'Patient'} />}
                  renderOption={({ key, ...props }, option, { inputValue }) => (
                    <AutocompleteOptions option={`${option.firstName} ${option.lastName}`} value={inputValue} key={key} {...props} />
                  )}
                />
              );
            }}
          />
          <Controller
            name={'cameras'}
            control={control}
            render={({ field }) => {
              return (
                <Autocomplete
                  value={field.value}
                  onChange={(_: any, value: Camera[] | null) => field.onChange(value)}
                  id={'cameras-select'}
                  options={camerasData}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(camera: Camera) => camera.name}
                  renderInput={(params) => <TextField {...params} label={'Cameras'} />}
                  multiple
                  renderOption={({ key, ...props }, option, { inputValue }) => (
                    <AutocompleteOptions option={option.name} value={inputValue} key={key} {...props} />
                  )}
                />
              );
            }}
          />
          <Controller
            name={'nvrs'}
            control={control}
            render={({ field }) => {
              return (
                <Autocomplete
                  value={field.value}
                  onChange={(_: any, value: Nvr[] | null) => field.onChange(value)}
                  id={'nvrs-select'}
                  options={nvrsData}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(nvr: Nvr) => nvr.name}
                  renderInput={(params) => <TextField {...params} label={'NVRs'} />}
                  multiple
                  renderOption={({ key, ...props }, option, { inputValue }) => (
                    <AutocompleteOptions option={option.name} value={inputValue} key={key} {...props} />
                  )}
                />
              );
            }}
          />
        </Stack>
      )}
    </FormDialog>
  );
};

export default RouterForm;
